import { useState, useCallback, useRef } from "react";
import { App } from "antd";
import {
  getSchoolCalendar,
  initializeSchoolCalendar,
  updateSchoolSingleWeekDate,
  updateSchoolMultipleWeekDates,
  syncSchoolCalendarWithGeneral,
  syncClassroomCalendarWithGeneral,
  syncClassroomCalendarWithSchool,
  divideCalendarByClassrooms,
  unifyCalendarsByClassrooms,
  getClassroomCalendar,
  updateClassroomCalendarDates,
  checkSchoolDivisionStatus,
  getResolvedClassroomCalendar,
  createProgramCalendarOverride,
  removeProgramCalendarOverride,
  updateProgramCalendarWeekDate,
  createClassroomCalendar,
} from "../services/calendar/calendar.service";
import {
  CalendarEntity,
  CalendarSource,
  EducationLevel,
  UpdateWeekDateRequest,
  UpdateMultipleWeeksRequest,
} from "../services/calendar/calendar.types";
import { CalendarsByLevel } from "../store/slices/calendar";

// Same convention as the general-calendar slice: `null` = not fetched yet,
// `[]` = fetched and empty. One entry per education section (SCRUM-842).
const emptyCalendarsByLevel = (): CalendarsByLevel => ({
  KINDER: null,
  PRIMARIA: null,
});

export const useSchoolCalendar = (schoolId: number) => {
  // From App.useApp(): the static notification does not render under React 19.
  const { notification } = App.useApp();
  const [calendarsByLevel, setCalendarsByLevel] = useState<CalendarsByLevel>(
    emptyCalendarsByLevel
  );
  const [loading, setLoading] = useState(false);
  const [hasPersonalizedCalendarByLevel, setHasPersonalizedCalendarByLevel] =
    useState<Record<EducationLevel, boolean>>({
      KINDER: false,
      PRIMARIA: false,
    });
  const [isDividedByClassrooms, setIsDividedByClassrooms] = useState(false);
  // Division is classroom-tier and un-leveled, so it is checked once per
  // mount, not once per level fetch.
  const divisionCheckedRef = useRef(false);
  const [classroomCalendars, setClassroomCalendars] = useState<
    Record<number, CalendarEntity[]>
  >({});

  // Per-program calendar override state (classroom-group-model, PR-S2c-FE).
  // Ephemeral, tied to whichever (classroomId, programId) pair is currently
  // selected in the UI — not persisted in Redux, mirroring how
  // classroomCalendars above is also plain local state rather than a slice.
  const [programOverrideSource, setProgramOverrideSource] =
    useState<CalendarSource | null>(null);
  const [programOverrideCalendar, setProgramOverrideCalendar] = useState<
    CalendarEntity[]
  >([]);
  const [programOverrideLoading, setProgramOverrideLoading] = useState(false);

  // Helper functions
  const showNotification = useCallback(
    (
      type: "success" | "error" | "warning",
      message: string,
      description: string,
      duration: number = 3
    ) => {
      notification[type]({
        message,
        description,
        placement: "top",
        duration,
      });
    },
    [notification]
  );

  const filterSchoolCalendar = useCallback((calendars: CalendarEntity[]) => {
    return calendars.filter(
      (cal) => cal.classroom_id === null || cal.classroom_id === undefined
    );
  }, []);

  const handleError = useCallback(
    (error: any, defaultMessage: string) => {
      const errorMessage = error.response?.data?.message || defaultMessage;

      if (errorMessage.includes("ya tiene contenido semanal")) {
        showNotification(
          "warning",
          "Fecha no disponible",
          "Esa fecha ya tiene contenido semanal para desbloquear asignado. Elige otra fecha.",
          5
        );
      } else {
        showNotification("error", "Error", errorMessage);
      }
      console.error(defaultMessage, error);
    },
    [showNotification]
  );

  const withLoading = useCallback(
    async <T>(
      asyncFn: () => Promise<T>,
      skipLoading: boolean = false
    ): Promise<T | undefined> => {
      if (!skipLoading) setLoading(true);
      try {
        return await asyncFn();
      } finally {
        if (!skipLoading) setLoading(false);
      }
    },
    []
  );

  const fetchSchoolCalendar = useCallback(
    async (
      level: EducationLevel,
      availableClassrooms?: Array<{ id: number; name: string }>
    ) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          const calendarResponse = await getSchoolCalendar(schoolId, level);
          const schoolOnlyCalendar = filterSchoolCalendar(calendarResponse);
          setCalendarsByLevel((prev) => ({
            ...prev,
            [level]: schoolOnlyCalendar,
          }));

          const isPersonalized =
            schoolOnlyCalendar.length > 0 &&
            (schoolOnlyCalendar[0].calendar_type === "SCHOOL" ||
              (schoolOnlyCalendar[0].school_id === schoolId &&
                schoolOnlyCalendar[0].classroom_id === null));
          setHasPersonalizedCalendarByLevel((prev) => ({
            ...prev,
            [level]: isPersonalized,
          }));

          if (
            availableClassrooms &&
            availableClassrooms.length > 0 &&
            !divisionCheckedRef.current
          ) {
            divisionCheckedRef.current = true;
            const classroomIds = availableClassrooms.map((c) => c.id);
            const divisionStatus = await checkSchoolDivisionStatus(
              schoolId,
              classroomIds
            );
            setIsDividedByClassrooms(divisionStatus.isDivided);
          }
        } catch (error: any) {
          handleError(error, "Error fetching school calendar");
          showNotification(
            "error",
            "Error",
            "No se pudo cargar el calendario de la escuela. Por favor, intenta nuevamente."
          );
        }
      });
    },
    [schoolId, withLoading, filterSchoolCalendar, handleError, showNotification]
  );

  const initSchoolCalendar = useCallback(
    async (level: EducationLevel) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          await initializeSchoolCalendar(schoolId, level);
          await fetchSchoolCalendar(level);
          showNotification(
            "success",
            "Éxito",
            "Calendario personalizado creado copiando las fechas del calendario general",
            4
          );
        } catch (error: any) {
          handleError(
            error,
            "Error al crear el calendario personalizado de la escuela"
          );
        }
      });
    },
    [schoolId, fetchSchoolCalendar, withLoading, showNotification, handleError]
  );

  const syncWithGeneralCalendar = useCallback(
    async (level: EducationLevel) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          await syncSchoolCalendarWithGeneral(schoolId, level);
          await fetchSchoolCalendar(level);
          showNotification(
            "success",
            "Éxito",
            "Calendario de la escuela sincronizado con el calendario general correctamente"
          );
        } catch (error: any) {
          handleError(
            error,
            "Error al sincronizar el calendario con el general"
          );
        }
      });
    },
    [schoolId, fetchSchoolCalendar, withLoading, showNotification, handleError]
  );

  const syncClassroomWithGeneral = useCallback(
    async (classroomId: number) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          const updatedCalendar = await syncClassroomCalendarWithGeneral(
            schoolId,
            classroomId
          );
          setClassroomCalendars((prev) => ({
            ...prev,
            [classroomId]: updatedCalendar,
          }));
          showNotification(
            "success",
            "Éxito",
            "Calendario del clase sincronizado con el calendario general correctamente"
          );
        } catch (error: any) {
          handleError(
            error,
            "Error al sincronizar el calendario del clase con el general"
          );
        }
      });
    },
    [schoolId, withLoading, showNotification, handleError]
  );

  const syncClassroomWithSchool = useCallback(
    async (classroomId: number) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          const updatedCalendar = await syncClassroomCalendarWithSchool(
            schoolId,
            classroomId
          );
          setClassroomCalendars((prev) => ({
            ...prev,
            [classroomId]: updatedCalendar,
          }));
          showNotification(
            "success",
            "Éxito",
            "Calendario del clase sincronizado con el calendario de la escuela correctamente"
          );
        } catch (error: any) {
          handleError(
            error,
            "Error al sincronizar el calendario del clase con la escuela"
          );
        }
      });
    },
    [schoolId, withLoading, showNotification, handleError]
  );

  const updateSchoolWeekDateHandler = useCallback(
    async (
      level: EducationLevel,
      data: UpdateWeekDateRequest | UpdateMultipleWeeksRequest
    ) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          const updatedCalendar =
            "weeks" in data
              ? await updateSchoolMultipleWeekDates({
                  ...data,
                  school_id: schoolId,
                  education_level: level,
                })
              : await updateSchoolSingleWeekDate({
                  ...data,
                  school_id: schoolId,
                  education_level: level,
                });

          const schoolOnlyCalendar = filterSchoolCalendar(updatedCalendar);
          setCalendarsByLevel((prev) => ({
            ...prev,
            [level]: schoolOnlyCalendar,
          }));
          showNotification(
            "success",
            "Éxito",
            "Fecha de semana actualizada correctamente"
          );
        } catch (error: any) {
          handleError(error, "Error al actualizar la fecha de la semana");
          throw error;
        }
      });
    },
    [schoolId, withLoading, filterSchoolCalendar, showNotification, handleError]
  );

  const divideByClassrooms = useCallback(
    async (classroomIds: number[]) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          await divideCalendarByClassrooms(schoolId, classroomIds);
          setIsDividedByClassrooms(true);
          showNotification(
            "success",
            "Éxito",
            "El calendario ha sido dividido por clases correctamente",
            4
          );
        } catch (error: any) {
          handleError(error, "Error al dividir el calendario por clases");
          throw error;
        }
      });
    },
    [schoolId, withLoading, showNotification, handleError]
  );

  const unifyCalendars = useCallback(
    async (classroomIds: number[]) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          await unifyCalendarsByClassrooms(schoolId, classroomIds);
          setIsDividedByClassrooms(false);
          setClassroomCalendars({});
          showNotification(
            "success",
            "Éxito",
            "Los calendarios han sido unificados correctamente",
            4
          );
        } catch (error: any) {
          handleError(error, "Error al unificar los calendarios");
          throw error;
        }
      });
    },
    [schoolId, withLoading, showNotification, handleError]
  );

  const fetchClassroomCalendar = useCallback(
    async (classroomId: number) => {
      if (!schoolId) return [];

      try {
        const classroomCalendar = await getClassroomCalendar(schoolId, classroomId);
        setClassroomCalendars((prev) => ({
          ...prev,
          [classroomId]: classroomCalendar,
        }));
        return classroomCalendar;
      } catch (error: any) {
        handleError(error, "Error al obtener el calendario del clase");
        return [];
      }
    },
    [schoolId, handleError]
  );

  /**
   * Una clase sin calendario propio hereda el de la escuela, y toda accion con
   * alcance de clase le responde 400 ("does not have a custom calendar"). Esto
   * le crea el propio copiando las fechas de la escuela.
   */
  const createClassroomCalendarForClass = useCallback(
    async (classroomId: number) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          const created = await createClassroomCalendar(schoolId, classroomId);
          setClassroomCalendars((prev) => ({
            ...prev,
            [classroomId]: created,
          }));
          showNotification(
            "success",
            "Exito",
            "La clase ya tiene su propio calendario, copiado del calendario de la escuela",
            4
          );
        } catch (error: any) {
          handleError(error, "Error al crear el calendario de la clase");
          throw error;
        }
      });
    },
    [schoolId, withLoading, showNotification, handleError]
  );

  const updateClassroomWeekDate = useCallback(
    async (
      classroomId: number,
      data: UpdateWeekDateRequest | UpdateMultipleWeeksRequest
    ) => {
      if (!schoolId) return;

      await withLoading(async () => {
        try {
          const updatedCalendar = await updateClassroomCalendarDates(
            schoolId,
            classroomId,
            data
          );
          setClassroomCalendars((prev) => ({
            ...prev,
            [classroomId]: updatedCalendar,
          }));
          showNotification(
            "success",
            "Éxito",
            "Fecha de semana actualizada correctamente",
            3
          );
        } catch (error: any) {
          handleError(error, "Error al actualizar la fecha de la semana");
          throw error;
        }
      });
    },
    [schoolId, withLoading, showNotification, handleError]
  );

  // ========== FUNCIONES DE CALENDARIO POR MATERIA (OVERRIDE) ==========

  const fetchResolvedProgramCalendar = useCallback(
    async (classroomId: number, programId: number) => {
      setProgramOverrideLoading(true);
      try {
        const resolved = await getResolvedClassroomCalendar(
          classroomId,
          programId
        );
        setProgramOverrideSource(resolved.source);
        setProgramOverrideCalendar(resolved.weeks);
        return resolved;
      } catch (error: any) {
        handleError(error, "Error al obtener el calendario de la asignatura");
        return undefined;
      } finally {
        setProgramOverrideLoading(false);
      }
    },
    [handleError]
  );

  const createProgramOverride = useCallback(
    async (classroomId: number, programId: number) => {
      await withLoading(async () => {
        try {
          const weeks = await createProgramCalendarOverride(
            classroomId,
            programId
          );
          setProgramOverrideSource("PROGRAM_OVERRIDE");
          setProgramOverrideCalendar(weeks);
          showNotification(
            "success",
            "Éxito",
            "Calendario creado para esta asignatura copiando el calendario del grupo",
            4
          );
        } catch (error: any) {
          handleError(error, "Error al crear el calendario de la asignatura");
          throw error;
        }
      });
    },
    [withLoading, showNotification, handleError]
  );

  const removeProgramOverride = useCallback(
    async (classroomId: number, programId: number) => {
      await withLoading(async () => {
        try {
          await removeProgramCalendarOverride(classroomId, programId);
          setProgramOverrideSource(null);
          setProgramOverrideCalendar([]);
          showNotification(
            "success",
            "Éxito",
            "Se restauró el calendario del grupo para esta asignatura",
            4
          );
        } catch (error: any) {
          handleError(error, "Error al volver al calendario del grupo");
          throw error;
        }
      });
    },
    [withLoading, showNotification, handleError]
  );

  const updateProgramWeekDate = useCallback(
    async (
      classroomId: number,
      programId: number,
      data: UpdateWeekDateRequest | UpdateMultipleWeeksRequest
    ) => {
      await withLoading(async () => {
        try {
          const updated = await updateProgramCalendarWeekDate(
            classroomId,
            programId,
            data
          );
          setProgramOverrideCalendar(updated);
          showNotification(
            "success",
            "Éxito",
            "Fecha de semana actualizada correctamente",
            3
          );
        } catch (error: any) {
          handleError(error, "Error al actualizar la fecha de la semana");
          throw error;
        }
      });
    },
    [withLoading, showNotification, handleError]
  );

  const clearProgramOverrideState = useCallback(() => {
    setProgramOverrideSource(null);
    setProgramOverrideCalendar([]);
  }, []);

  return {
    calendarsByLevel,
    loading,
    hasPersonalizedCalendarByLevel,
    isDividedByClassrooms,
    classroomCalendars,
    fetchSchoolCalendar,
    initSchoolCalendar,
    syncWithGeneralCalendar,
    syncClassroomWithGeneral,
    syncClassroomWithSchool,
    updateSchoolWeekDate: updateSchoolWeekDateHandler,
    divideByClassrooms,
    unifyCalendars,
    fetchClassroomCalendar,
    createClassroomCalendarForClass,
    updateClassroomWeekDate,
    programOverrideSource,
    programOverrideCalendar,
    programOverrideLoading,
    fetchResolvedProgramCalendar,
    createProgramOverride,
    removeProgramOverride,
    updateProgramWeekDate,
    clearProgramOverrideState,
  };
};
